function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * 
 * @file Helper for the box folder api
 * @author Box
 */
import noop from 'lodash/noop';
import flatten from '../utils/flatten';
import { FOLDER_FIELDS_TO_FETCH } from '../utils/fields';
import { getBadItemError } from '../utils/error';
import Item from './Item';
import FileAPI from './File';
import WebLinkAPI from './WebLink';
import { CACHE_PREFIX_FOLDER, ERROR_CODE_FETCH_FOLDER, ERROR_CODE_CREATE_FOLDER, FIELD_REPRESENTATIONS, X_REP_HINT_HEADER_DIMENSIONS_DEFAULT } from '../constants';

class Folder extends Item {
  constructor() {
    super(...arguments);

    _defineProperty(this, "folderSuccessHandler", (_ref) => {
      let {
        data
      } = _ref;

      if (this.isDestroyed()) {
        return;
      }

      const {
        item_collection
      } = data;

      if (!item_collection) {
        throw getBadItemError();
      }

      const {
        entries,
        total_count,
        limit,
        offset
      } = item_collection;

      if (!Array.isArray(entries) || typeof total_count !== 'number' || typeof limit !== 'number' || typeof offset !== 'number') {
        throw getBadItemError();
      }

      const flattened = flatten(entries, new Folder(this.options), new FileAPI(this.options), new WebLinkAPI(this.options));
      this.itemCache = (this.itemCache || []).concat(flattened);
      this.getCache().set(this.key, _objectSpread({}, data, {
        item_collection: _objectSpread({}, item_collection, {
          entries: this.itemCache
        })
      }));
      this.finish();
    });

    _defineProperty(this, "folderDetailsSuccessHandler", (_ref2) => {
      let {
        data
      } = _ref2;

      if (this.isDestroyed()) {
        return;
      }

      const cachedEntry = this.getCache().get(this.key);

      const updatedCacheEntry = _objectSpread({}, cachedEntry, {}, data);

      this.getCache().set(this.key, updatedCacheEntry);
      this.successCallback(updatedCacheEntry);
    });

    _defineProperty(this, "createSuccessHandler", (_ref3) => {
      let {
        data
      } = _ref3;
      const {
        id: childId
      } = data;

      if (this.isDestroyed() || !childId) {
        return;
      }

      const childKey = this.getCacheKey(childId);
      const cache = this.getCache();
      const parent = cache.get(this.key);

      if (!parent) {
        this.successCallback(data);
        return;
      }

      const {
        item_collection
      } = parent;

      if (!item_collection) {
        throw getBadItemError();
      }

      const {
        total_count,
        entries
      } = item_collection;

      if (!Array.isArray(entries) || typeof total_count !== 'number') {
        throw getBadItemError();
      }

      cache.set(childKey, data);
      item_collection.entries = [childKey].concat(entries);
      item_collection.total_count = total_count + 1;
      this.successCallback(data);
    });
  }

  /**
   * @property {string}
   */

  /**
   * @property {string}
   */

  /**
   * @property {number}
   */

  /**
   * @property {number}
   */

  /**
   * @property {string}
   */

  /**
   * @property {string}
   */

  /**
   * @property {Array}
   */

  /**
   * @property {Function}
   */

  /**
   * @property {Function}
   */

  /**
   * Creates a key for the cache
   *
   * @param {string} id folder id
   * @return {string} key
   */
  getCacheKey(id) {
    return "".concat(CACHE_PREFIX_FOLDER).concat(id);
  }
  /**
   * Base URL for folder api
   *
   * @param {string} [id] optional file id
   * @return {string} base url for files
   */


  getUrl(id) {
    const suffix = id ? "/".concat(id) : '';
    return "".concat(this.getBaseApiUrl(), "/folders").concat(suffix);
  }
  /**
   * Tells if a folder has its items all loaded
   *
   * @return {boolean} if items are loaded
   */


  isLoaded() {
    const cache = this.getCache();
    return cache.has(this.key);
  }
  /**
   * Composes and returns the results
   *
   * @return {void}
   */


  finish() {
    if (this.isDestroyed()) {
      return;
    }

    const cache = this.getCache();
    const folder = cache.get(this.key);
    const {
      id,
      name,
      permissions,
      path_collection,
      item_collection
    } = folder;

    if (!item_collection || !path_collection) {
      throw getBadItemError();
    }

    const {
      entries,
      offset,
      total_count
    } = item_collection;

    if (!Array.isArray(entries) || typeof total_count !== 'number') {
      throw getBadItemError();
    }

    const collection = {
      id,
      name,
      offset,
      percentLoaded: 100,
      permissions,
      boxItem: folder,
      breadcrumbs: path_collection.entries,
      items: entries.map(key => cache.get(key)),
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      totalCount: total_count
    };
    this.successCallback(collection);
  }
  /**
   * Handles the folder fetch response
   *
   * @param {Object} response
   * @return {void}
   */


  /**
   * Does the network request for fetching a folder
   *
   * @param {Array<string>|void} fields - Optionally include specific fields
   * @return {Promise}
   */
  folderRequest() {
    let {
      fields,
      noPagination
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let successHandler = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.folderSuccessHandler;

    if (this.isDestroyed()) {
      return Promise.reject();
    }

    const requestFields = fields || FOLDER_FIELDS_TO_FETCH;
    this.errorCode = ERROR_CODE_FETCH_FOLDER;
    let params = {
      fields: requestFields.toString()
    };

    if (!noPagination) {
      params = _objectSpread({}, params, {
        direction: this.sortDirection.toLowerCase(),
        limit: this.limit,
        offset: this.offset,
        fields: requestFields.toString(),
        sort: this.sortBy.toLowerCase()
      });
    }

    return this.xhr.get({
      url: this.getUrl(this.id),
      params,
      headers: requestFields.includes(FIELD_REPRESENTATIONS) ? {
        'X-Rep-Hints': X_REP_HINT_HEADER_DIMENSIONS_DEFAULT
      } : {}
    }).then(successHandler).catch(this.errorHandler);
  }
  /**
   * Gets a box folder properties. If you want to get the items, you should use `getFolder`
   *
   * @param {string} id - Folder id
   * @param {Function} successCallback - Function to call with results
   * @param {Function} errorCallback - Function to call with errors
   * @param {Object} options - Options
   * @returns {void}
   */


  getFolderFields(id, successCallback, errorCallback) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};

    if (this.isDestroyed()) {
      return;
    } // Save references


    this.id = id;
    this.key = this.getCacheKey(id);
    this.successCallback = successCallback;
    this.errorCallback = errorCallback;
    this.folderRequest(_objectSpread({}, options, {
      noPagination: true
    }), this.folderDetailsSuccessHandler);
  }
  /**
   * Gets a box folder and its items
   *
   * @param {string} id - Folder id
   * @param {number} limit - maximum number of items to retrieve
   * @param {number} offset - starting index from which to retrieve items
   * @param {string} sortBy - sort by field
   * @param {string} sortDirection - sort direction
   * @param {Function} successCallback - Function to call with results
   * @param {Function} errorCallback - Function to call with errors
   * @param {boolean|void} [options.fields] - Optionally include specific fields
   * @param {boolean|void} [options.forceFetch] - Optionally Bypasses the cache
   * @param {boolean|void} [options.refreshCache] - Optionally Updates the cache
   * @return {void}
   */


  getFolder(id, limit, offset, sortBy, sortDirection, successCallback, errorCallback) {
    let options = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : {};

    if (this.isDestroyed()) {
      return;
    } // Save references


    this.id = id;
    this.key = this.getCacheKey(id);
    this.limit = limit;
    this.offset = offset;
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
    this.successCallback = successCallback;
    this.errorCallback = errorCallback; // Clear the cache if needed

    if (options.forceFetch) {
      this.getCache().unset(this.key);
    } // Return the Cache value if it exists


    if (this.isLoaded()) {
      this.finish();
      return;
    } // Make the XHR request


    this.folderRequest(options);
  }
  /**
   * API to rename an Item
   *
   * @param {string} id - parent folder id
   * @param {string} name - new folder name
   * @param {Function} successCallback - success callback
   * @param {Function} errorCallback - error callback
   * @return {void}
   */


  /**
   * Does the network request for fetching a folder
   *
   * @return {void}
   */
  folderCreateRequest(name) {
    if (this.isDestroyed()) {
      return Promise.reject();
    }

    this.errorCode = ERROR_CODE_CREATE_FOLDER;
    const url = "".concat(this.getUrl(), "?fields=").concat(FOLDER_FIELDS_TO_FETCH.toString());
    return this.xhr.post({
      url,
      data: {
        name,
        parent: {
          id: this.id
        }
      }
    }).then(this.createSuccessHandler).catch(this.errorHandler);
  }
  /**
   * API to create a folder
   *
   * @param {string} id - parent folder id
   * @param {string} name - new folder name
   * @param {Function} successCallback - success callback
   * @param {Function} errorCallback - error callback
   * @return {void}
   */


  create(id, name, successCallback) {
    let errorCallback = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;

    if (this.isDestroyed()) {
      return;
    }

    this.id = id;
    this.key = this.getCacheKey(id);
    this.successCallback = successCallback;
    this.errorCallback = errorCallback;
    this.folderCreateRequest(name);
  }

}

export default Folder;
//# sourceMappingURL=Folder.js.map